import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import RecruiterStepModal from '../RecruiterStepModal';
import { useLocation, useParams } from 'react-router-dom';
import { Tab, Tabs, Modal, Button } from 'react-bootstrap';
import { fetchUserData } from '../PublicPreview/api';
import { fetchPortfolioData, fetchPromptsData } from './api';
import PublicUserInformationList from '../PublicPreview/PublicUserInformationList';
import PublicUserPanel from '../PublicPreview/PublicUserPanel';
import PublicUserProfileTabContent from './PublicUserProfileTabContent';
import { PageBusyLoader } from '../ui/PageBusyLoader';
import CandidatePublicScreeningPrompts from './CandidatePublicScreeningPrompts';
import { API_ROUTES } from '../../config/api-routes';
import axiosInstance, { startRefreshTimer } from '../../api/api';
import logo from '../../images/SKIP.svg';
import AppBackButton from '../common/AppBackButton';
import { publicProfileUsernameFromLocation } from '../../utils/publicProfileAccess';
import PublicVisitorIdentifyModal from './PublicVisitorIdentifyModal';
import ResumePreviewModal from './ResumePreviewModal';
import PublicProfileSidebarExtras from './PublicProfileSidebarExtras';
import { readPublicViewerSession } from '../../utils/publicViewerSession';
import { EMPTY_PUBLIC_PORTFOLIO, normalizePublicPortfolio } from '../../utils/publicProfileDisplay';
import './UserProfile.css';

const PublicUserProfile: React.FC = () => {
  const [key, setKey] = useState<string>('prompts');
  const [isPortfolioPresent, setIsPortfolioPresent] = useState<boolean>(false);
  const [whoAmI, setWhoAmI] = useState<any>(null);
  const [show, setShow] = useState<boolean>(false);
  const [showRecruiterWelcome, setShowRecruiterWelcome] = useState(false);
  const [loader, setLoader] = useState<boolean>(true);
  const [iNeedAccessToView, setINeedAccessToView] = useState<'YES' | 'NO'>('NO');
  const [userData, setUserData] = useState<any>(null);
  const [promptsData, setPromptsData] = useState<any[]>([]);
  const [portfolioData, setPortfolioData] = useState(EMPTY_PUBLIC_PORTFOLIO);
  const trackedProfileViewForCandidate = useRef<number | null>(null);
  const [showIdentifyModal, setShowIdentifyModal] = useState(false);
  const [showResumePreview, setShowResumePreview] = useState(false);

  const location = useLocation();
  const { username: usernameParam } = useParams<{ username: string }>();
  const username = useMemo(
    () => publicProfileUsernameFromLocation(location.pathname, usernameParam),
    [location.pathname, usernameParam]
  );
  const searchParams = new URLSearchParams(location.search);
  const recruiterToken = searchParams.get('recruitertoken');
  const shareToken = searchParams.get('share_token');
  const forcePreviewMode = searchParams.get('preview') === '1';

  const profileAccessToken = recruiterToken || shareToken;
  const profileAccessAsRecruiter = Boolean(recruiterToken);

  const getData = useCallback(async () => {
    if (!username) return;
    setLoader(true);
    const tokenOpts = { asRecruiterToken: profileAccessAsRecruiter };
    try {
      const [user, portfolio, prompts] = await Promise.all([
        fetchUserData(username, profileAccessToken, tokenOpts),
        fetchPortfolioData(username, profileAccessToken, tokenOpts),
        fetchPromptsData(username, profileAccessToken, tokenOpts),
      ]);

      setUserData(user || null);
      setPromptsData(Array.isArray(prompts) ? prompts : []);

      if (portfolio) {
        const normalized = normalizePublicPortfolio(portfolio);
        setPortfolioData(normalized);
        const hasPortfolioContent =
          Boolean(normalized.overview?.trim()) ||
          normalized.projects.length > 0 ||
          normalized.references.length > 0 ||
          normalized.languages.length > 0 ||
          normalized.certifications.length > 0 ||
          normalized.organizations.length > 0 ||
          normalized.accomplishments.length > 0 ||
          normalized.workCulture.length > 0 ||
          normalized.isLanguagesEnabled !== false ||
          normalized.isWorkCultureEnabled !== false ||
          normalized.isReferencesEnabled !== false;
        setIsPortfolioPresent(hasPortfolioContent);
      } else {
        setPortfolioData(EMPTY_PUBLIC_PORTFOLIO);
        setIsPortfolioPresent(false);
      }
    } catch (err) {
      console.error('Failed to fetch public profile data', err);
      setUserData(null);
      setPromptsData([]);
      setPortfolioData(EMPTY_PUBLIC_PORTFOLIO);
      setIsPortfolioPresent(false);
    } finally {
      setLoader(false);
    }
  }, [username, profileAccessToken, profileAccessAsRecruiter]);

  useEffect(() => {
    const doAutoLogin = async () => {
      if (!recruiterToken) return;
      try {
        const response = await axiosInstance.post(API_ROUTES.RECRUITER.VERIFY_RECRUITER_AND_LOGIN, {
          code: recruiterToken,
        });
        const data = response.data;
        if (response && response.status === 200) {
          localStorage.setItem('accessToken', data.access_token);
          localStorage.setItem('refreshToken', data.refresh_token);
          localStorage.setItem('userType', data.usertype || 'recruiter');
          startRefreshTimer();
        }
      } catch (err) {
        console.error('Recruiter auto-login failed', err);
      }
    };
    doAutoLogin();
  }, [recruiterToken]);

  useEffect(() => {
    const fetchWhoAmI = async () => {
      try {
        const response = await axiosInstance.get(API_ROUTES.USER.GET_CURRENT_USER);
        if (response && response.status === 200) {
          setWhoAmI(response.data?.user || null);
        }
      } catch {
        setWhoAmI(null);
      }
    };
    fetchWhoAmI();
  }, []);

  const viewerType = useMemo<'recruiter' | 'candidate_self' | 'anonymous'>(() => {
    if (forcePreviewMode) return 'candidate_self';
    if (whoAmI?.username && whoAmI.username === username) return 'candidate_self';
    const storedType = localStorage.getItem('userType');
    if (storedType === 'recruiter' || whoAmI?.usertype === 'recruiter') return 'recruiter';
    return 'anonymous';
  }, [forcePreviewMode, whoAmI, username]);

  useEffect(() => {
    const checkAccessToView = async () => {
      const candidateId = userData?.id ?? userData?.user_id;
      if (!candidateId) return;

      // Owner preview and valid share links should not trigger access modal.
      if (forcePreviewMode || shareToken || (whoAmI?.username && whoAmI.username === username)) {
        setINeedAccessToView('NO');
        setShow(false);
        return;
      }

      try {
        const response = await axiosInstance.post(API_ROUTES.RECRUITER.CONFIRM_ACCESS, {
          candidateUserId: candidateId,
        });
        if (response && response.status === 200) {
          setINeedAccessToView('NO');
          setShow(false);
        }
      } catch {
        // For shared-token path we still avoid request modal noise.
        if (recruiterToken) {
          setINeedAccessToView('NO');
          setShow(false);
          return;
        }
        setINeedAccessToView('YES');
        setShow(true);
      }
    };
    checkAccessToView();
  }, [forcePreviewMode, userData, whoAmI, username, recruiterToken, shareToken]);

  useEffect(() => {
    const trackProfileView = async () => {
      const candidateId = userData?.id ?? userData?.user_id;
      if (!candidateId) return;
      if (whoAmI?.username === username) return;
      if (viewerType !== 'recruiter') return;
      if (trackedProfileViewForCandidate.current === candidateId) return;
      try {
        const userAgent = navigator.userAgent;
        const platform: 'web' | 'android' | 'ios' | 'unknown' = /Android/i.test(userAgent)
          ? 'android'
          : /iPhone|iPad|iPod/i.test(userAgent)
            ? 'ios'
            : /Windows|Macintosh|Linux/i.test(userAgent)
              ? 'web'
              : 'unknown';
        await axiosInstance.post(API_ROUTES.ANALYTICS.TRACK_PROFILE_VIEW, {
          candidateId: userData.id,
          viewerType,
          source: recruiterToken ? 'email' : 'direct',
          deviceType: /Mobile|Android|iPhone/i.test(userAgent) ? 'mobile' : 'desktop',
          platform,
        });
        trackedProfileViewForCandidate.current = candidateId;
      } catch (err) {
        console.error('Failed to track profile view:', err);
      }
    };
    trackProfileView();
  }, [userData, whoAmI, username, viewerType, recruiterToken]);

  useEffect(() => {
    if (username) getData();
  }, [getData, username]);

  useEffect(() => {
    if (!username || forcePreviewMode || recruiterToken || shareToken) {
      setShowIdentifyModal(false);
      return;
    }
    const existing = readPublicViewerSession(username);
    if (existing) {
      setShowIdentifyModal(false);
      return;
    }
    setShowIdentifyModal(true);
  }, [username, forcePreviewMode, recruiterToken, shareToken]);

  useEffect(() => {
    if (!recruiterToken || !username) return;
    const seenKey = `ss_recruiter_welcome_seen_${username}`;
    if (!localStorage.getItem(seenKey) && iNeedAccessToView === 'YES') {
      setShowRecruiterWelcome(true);
      localStorage.setItem(seenKey, '1');
    }
  }, [recruiterToken, username, iNeedAccessToView]);

  if (loader) {
    return (
      <PageBusyLoader
        pageName={forcePreviewMode ? 'Profile preview' : 'Profile'}
        className="page-busy-loader--public-profile"
      />
    );
  }

  const isOwnProfile = forcePreviewMode || whoAmI?.username === username;
  const hasFullAccess =
    Boolean(userData?.has_full_access || userData?.hasFullAccess) ||
    iNeedAccessToView === 'NO' ||
    Boolean(recruiterToken || shareToken);
  const candidateDisplayName =
    `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || username || 'Candidate';

  return (
    <>
      <div
        className={`public-profile-shell${
          forcePreviewMode
            ? ' public-profile-shell--preview public-profile-shell--recruiter-view'
            : ''
        }`}
      >
        <div className="public-profile-top-accent" />
        {forcePreviewMode ? (
          <div className="public-profile-preview-top">
            <AppBackButton to="/dashboard" />
          </div>
        ) : (
          <div className="public-profile-shell-header">
            <div className="public-profile-brand" aria-label="SkipScreen brand">
              <img src={logo} alt="SkipScreen Logo" className="public-profile-logo" />
            </div>
          </div>
        )}

        {forcePreviewMode ? (
          <div className="public-profile-preview-banner">
            <div className="public-profile-preview-banner-inner">
              <span>
                Preview mode — this is how recruiters see your public profile (read-only).
              </span>
            </div>
          </div>
        ) : null}

        <div className="public-profile-shell-body">
          <div className="public-profile-shell-layout">
            <aside className="public-profile-left-rail">
              <PublicUserPanel
                userData={userData}
                jobTitle={promptsData?.[0]?.jobTitle || userData?.jobTitle || ''}
                iNeedAccessToView={iNeedAccessToView}
                iOwnTheAccount={isOwnProfile}
                hasFullAccess={hasFullAccess}
                onResumePreview={() => setShowResumePreview(true)}
                onRequestFullAccess={() => setShow(true)}
              />
              <PublicUserInformationList
                userData={userData}
                iOwnTheAccount={isOwnProfile}
                iNeedAccessToView={iNeedAccessToView}
                candidateId={userData?.id}
                viewerType={viewerType}
                hasFullAccess={hasFullAccess}
              />
              <PublicProfileSidebarExtras userData={userData} />
            </aside>

            <section className="public-profile-main-rail">
              <div className="public-profile-tab-shell">
                <Tabs
                  id="controlled-tab"
                  activeKey={key}
                  onSelect={(k) => setKey(k as string)}
                  className="public-profile-tabs"
                >
                  <Tab eventKey="prompts" title="Screening Prompts">
                    <CandidatePublicScreeningPrompts
                      prompts={promptsData}
                      iOwnTheAccount={isOwnProfile}
                      iNeedAccessToView={iNeedAccessToView}
                      hasFullAccess={hasFullAccess}
                      setPromptsData={() => {}}
                      candidateId={userData?.id}
                      viewerType={viewerType}
                      shareToken={recruiterToken || shareToken}
                      candidateUsername={userData?.username || username || ''}
                      candidateDisplayName={candidateDisplayName}
                      onRequestFullAccess={() => setShow(true)}
                    />
                  </Tab>
                  <Tab eventKey="portfolio" title={`Portfolio${!isPortfolioPresent ? ' (0)' : ''}`}>
                    {!isPortfolioPresent ? (
                      <div className="public-profile-empty-state">
                        This candidate has not created a portfolio yet.
                      </div>
                    ) : (
                      <PublicUserProfileTabContent
                        portfolio={portfolioData}
                        setPortfolioData={(_) => {}}
                        setSavePortfolioButton={(_) => {}}
                        savePortfolioButton={false}
                        deletePortfolioContext={false}
                        getData={getData}
                        viewerType={viewerType}
                        hasFullAccess={hasFullAccess}
                      />
                    )}
                  </Tab>
                </Tabs>
              </div>
            </section>
          </div>
        </div>
      </div>

      {!isOwnProfile && show && iNeedAccessToView === 'YES' && (
        <RecruiterStepModal
          show={show}
          candidateName={userData?.firstName + ' ' + userData?.lastName || 'Candidate'}
          candidateUsername={username}
          onClose={() => setShow(false)}
        />
      )}

      {!forcePreviewMode && !recruiterToken && !shareToken ? (
        <PublicVisitorIdentifyModal
          show={showIdentifyModal}
          username={username}
          onIdentified={() => {
            setShowIdentifyModal(false);
          }}
        />
      ) : null}

      <ResumePreviewModal
        show={showResumePreview}
        username={username}
        shareToken={recruiterToken || shareToken}
        onHide={() => setShowResumePreview(false)}
      />

      <Modal show={showRecruiterWelcome} onHide={() => setShowRecruiterWelcome(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Welcome to SkipScreen 👋</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have temporary access to this candidate profile. Explore prompts, videos, and
          supporting documents to pre-screen faster.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => window.open('/', '_self')}>
            Create Free Account
          </Button>
          <Button variant="outline-secondary" onClick={() => setShowRecruiterWelcome(false)}>
            Continue as Guest
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PublicUserProfile;
